import { Box, CircularProgress, Grid, Typography } from '@material-ui/core';
import {
  CheckBox,
  DateField,
  DefaultButton,
  DefaultCard,
  DisplayItem,
  FormValueTypes,
  PaddedContent,
  useFormHook,
} from 'design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import {
  GeneralCatalogItem,
  Quotation,
  RequestIndicatorTypes,
} from 'data-layer-cr/dist/models';
import {
  setRequestStatus,
  useNetworkObserver,
} from 'data-layer-cr/dist/network';

// Redux
import { requestBanks } from 'src/store/creditorCatalog/actions';
import {
  resetCedulaFiles,
  setDataFields,
  setFinalBeneficiary,
  setIdentificationBase64,
  setIdentificationOptionalBase64,
  setLegalBeneficiaries,
  setNewDataFields,
  updateEmissionByPage,
} from 'src/store/emissionPolicy/actions';
import {
  getBeneficiaryBirthPlace,
  getBeneficiaryCities,
  getBeneficiaryCountries,
  getBeneficiaryCountryStates,
  getBeneficiaryNationality,
  getBeneficiaryOccupations,
  getBeneficiaryTownships,
  getBirthPlaces,
  getCities,
  getCivilStatus,
  getCountries,
  getCountryStates,
  getEconomicAcitivies,
  getRestrictedBirthPlaces,
  getTownships,
} from 'src/store/legalEntityCatalog/actions';
import {
  requestSearchPerson,
  requestSearchValidatePerson,
  requestSearchValidateShareholder,
} from 'src/store/person/actions';
import {
  REQUEST_SEARCH_PERSON,
  REQUEST_SEARCH_VALIDATE_SHAREHOLDER,
} from 'src/store/person/requestIdentifiers';
import { RootState } from 'src/store/rootReducer';

// Constants
import { COSTA_RICA } from 'src/constants/countryCode';
import {
  legalEntityList as legalEntityIdTypes,
  list as naturalPersonIdTypes,
} from 'src/constants/idTypes';
import { individuals, profiles } from 'src/constants/profileTypes';
import { civilStatus } from 'src/data/civilStatus.json';

// functions
import {
  findIdByCode,
  findNameByCode,
} from 'src/functions/mapCatatalogToDisplay';
import { resolvesToNotPep } from 'src/functions/resolveToNotPep';

// Models
import { format } from 'date-fns';
import EmissionDataFields, {
  LegalBeneficiary,
} from 'src/models/helperTypes/EmissionDataFields';
import FinalBeneficiary from 'src/models/helperTypes/FinalBeneficiary';
import PersonDataResponse from 'src/models/responseTypes/PersonValidationResponse';

// Methods
import { MultiFileLoader } from 'src/components/general';
import Tabs from 'src/components/general/CustomTabs';

import { cities as staticCities } from 'src/data/citiesCr.json';
import { states as staticStates } from 'src/data/statesCr.json';
import { townships as staticTownships } from 'src/data/townshipsCr.json';
import {
  addLegalPersonACSEL,
  addShareholderEquifax,
  updateToInitialValues,
} from 'src/functions/equifax';
import { personRequest } from 'src/services/equifax';
import FormShape from './FormShape';
import {
  parseEmissionData,
  parseFinalBeneficiaryData,
  parseInitialFormData,
} from './functions/dataMethods';
import makeValidationSchema, { PREFIX_KEY } from './makeValidationSchema';

// hooks
import {
  useBeneficiaryAddressInformation,
  useEconomicActivityInformation,
  useOccupationInformation,
} from '../hooks';
import usePersonDataCommon from '../hooks/usePersonDataCommon';
import { useLegalEntityValidator } from './hooks';

// subcomponents
import {
  FinalBeneficiaryCard,
  GeneralLegalFieldsCard,
  IdCard,
} from '../../components';
import {
  IdInputOptions,
  InstructionOptions,
} from '../../components/IdCard/IdCard';
import { makeAddressDataSection } from '../components/generationMethods';
import PersonValidationAlert from '../components/PersonValidationAlert';
import PersonValidationModal from '../components/PersonValidationModal';
import {
  BeneficiaryDialog,
  CreditorCard,
  LegalHeirsCards,
} from '../NaturalPersonData/components';
import PepCard from '../PepCard';
import { minDesktopCardHeight } from '../useBaseWrapperStyles';
import {
  makeEnterpriseDataSection,
  makeFundsOriginDataSection,
  makeShareholderDataSection,
} from './generationMethods';

/** Extracted to prevent sonarcloud from complaining */
function renderInputOptions(
  formatError: string,
  entityIsInvalid: boolean,
  requestingPerson: RequestIndicatorTypes,
): IdInputOptions {
  return {
    key: 'idNumber',
    label: 'Número de RUC',
    errorMessage: formatError,
    personIsInvalid: entityIsInvalid,
    requestingPerson,
  };
}

/** Extracted to prevent sonarcloud from complaining */
function renderInstructionOptions(
  messageLabel: JSX.Element,
): InstructionOptions {
  return {
    inputId: 'legalpersondata',
    headerTitle: '',
    statusMessage: messageLabel,
    contentTitle: 'Número de RUC',
    content: <></>,
    instructions: (
      <Typography
        id="sales-web-inspection-label-legalpersondatainstructiontext"
        variant="body2"
        align="center"
      >
        Sin espacios ni guiones (ej. 3XXXXXXXXX), tal como aparece en tu aviso
        de operaciones.
      </Typography>
    ),
  };
}

export const LegalPersonData = ({
  dataFields,
  finalBeneficiary,
  personData,
  quotation,
  setDataFieldsAction,
  setFinalBeneficiaryAction,
  requestBanksAction,
  onSubmit,
  requestUpdateEmission,
  resetRequestStatus,
  countries,
  countryStates,
  cities,
  townships,
  beneficiaryCountries,
  beneficiaryCountryStates,
  beneficiaryCities,
  beneficiaryTownships,
  beneficiaryNationality,
  beneficiaryBirthPlace,
  beneficiaryOccupations,
  economicActivities,
  getCountriesAction,
  getCountryStatesAction,
  getCitiesAction,
  getTownshipsAction,
  getBeneficiaryCountriesAction,
  getBeneficiaryCountryStatesAction,
  getBeneficiaryCitiesAction,
  getBeneficiaryTownshipsAction,
  getBeneficiaryBirthPlaceAction,
  getBeneficiaryNationalityAction,
  getBeneficiaryOccupationsAction,
  getEconomicActivitiesAction,
  validatePersonAction,
  resetCedulaFilesAction,
  cedulaFileName,
  cedulaFileBase64,
  cedulaOptionalFileName,
  cedulaOptionalFileBase64,
  uploadCedulaAction,
  uploadCedulaOptionalAction,
  setLegalBeneficiariesAction,
  legalBeneficiaries,
  banks,
  getBirthPlacesAction,
  getRestrictedBirthPlacesAction,
  birthPlaces,
  restrictedBirthPlaces,
  personValidateData,
  validateSearchPersonAction,
  shareholderValidateData,
  validateSearchShareholderAction,
}: Props): JSX.Element => {
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    requestBanksAction();
  }, [requestBanksAction]);

  /**
   * Load beneficiary cities/townships if they are missing from the persisted state
   * but we already have codes in finalBeneficiary. This handles the case where
   * legalEntityCatalog.beneficiaryCities/beneficiaryTownships were not persisted.
   */
  useEffect(() => {
    if (
      finalBeneficiary.cityId &&
      beneficiaryCities.length === 0 &&
      beneficiaryCountryStates.length > 0
    ) {
      const stateId = findIdByCode(
        beneficiaryCountryStates,
        finalBeneficiary.stateId,
      );
      if (stateId) getBeneficiaryCitiesAction(stateId);
    }

    if (
      finalBeneficiary.townshipId &&
      beneficiaryTownships.length === 0 &&
      beneficiaryCities.length > 0
    ) {
      const cityId = findIdByCode(beneficiaryCities, finalBeneficiary.cityId);
      if (cityId) getBeneficiaryTownshipsAction(cityId);
    }
  }, [
    finalBeneficiary.cityId,
    finalBeneficiary.stateId,
    finalBeneficiary.townshipId,
    beneficiaryCities,
    beneficiaryTownships.length,
    beneficiaryCountryStates,
    getBeneficiaryCitiesAction,
    getBeneficiaryTownshipsAction,
  ]);

  /**
   * Update beneficiaryCity and beneficiaryTownship in finalBeneficiary when catalogs load
   * and the names are empty. This ensures the names are resolved BEFORE the API request.
   */
  useEffect(() => {
    const updates: Partial<Pick<FinalBeneficiary, 'city' | 'township'>> = {};

    if (
      finalBeneficiary.cityId &&
      !finalBeneficiary.city &&
      beneficiaryCities.length > 0
    ) {
      const cityName = findNameByCode(
        beneficiaryCities,
        finalBeneficiary.cityId,
      );
      if (cityName) updates.city = cityName;
    }

    if (
      finalBeneficiary.townshipId &&
      !finalBeneficiary.township &&
      beneficiaryTownships.length > 0
    ) {
      const townshipName = findNameByCode(
        beneficiaryTownships,
        finalBeneficiary.townshipId,
      );
      if (townshipName) updates.township = townshipName;
    }

    if (Object.keys(updates).length > 0) {
      setFinalBeneficiaryAction({ ...finalBeneficiary, ...updates });
    }
  }, [
    finalBeneficiary.cityId,
    finalBeneficiary.city,
    finalBeneficiary.townshipId,
    finalBeneficiary.township,
    beneficiaryCities,
    beneficiaryTownships,
    setFinalBeneficiaryAction,
  ]);

  useEffect(() => {
    getBeneficiaryCountriesAction();
    getBeneficiaryCountryStatesAction();
    getBeneficiaryBirthPlaceAction();
    getBeneficiaryNationalityAction();
    getBeneficiaryOccupationsAction();
    getEconomicActivitiesAction();
  }, [
    getBeneficiaryCountriesAction,
    getBeneficiaryCountryStatesAction,
    getBeneficiaryBirthPlaceAction,
    getBeneficiaryOccupationsAction,
    getEconomicActivitiesAction,
    getBeneficiaryNationalityAction,
  ]);

  const isIndividualQuotation = useMemo(
    () => individuals.includes(quotation.quotationProfile.type as string),
    [quotation],
  );
  const isCollectiveQuotation = useMemo(
    () => quotation.quotationProfile.type === profiles.COLLECTIVE,
    [quotation],
  );

  const validationSchema = makeValidationSchema(
    isIndividualQuotation,
    quotation.summary.insuranceValue,
  );

  const requestingPerson = useNetworkObserver(REQUEST_SEARCH_PERSON);

  const [openLegalBeneficiaries, setOpenLegalBeneficiaries] = useState(false);
  const [showSaveLegalBeneficiaries, setShowSaveLegalBeneficiaries] =
    useState(false);

  // Agregated to manage origin occupation
  const [selectedOrigin, setSelectedOrigin] = useState<string>('');

  /**
   * Called when the form is submitted, and no errors occur
   */
  function handleFormSubmit(data: FormShape): void {
    const baseData = parseEmissionData(data, countries, civilStatus);
    const staticState = staticStates.find(
      (item) => item.code === baseData.addressState,
    );
    const staticCitiesForState = staticCities.filter(
      (item) => item.parentId === staticState?.id,
    );
    const staticCity = staticCitiesForState.find(
      (item) => item.code === baseData.addressCity,
    );
    const staticTownshipsForCity = staticTownships.filter(
      (item) => item.parentId === staticCity?.id,
    );
    const emission: EmissionDataFields = {
      ...baseData,
      addressCountryName:
        findNameByCode(countries, baseData.addressCountry) ||
        baseData.addressCountryName,
      addressStateName:
        findNameByCode(countryStates, baseData.addressState) ||
        baseData.addressStateName,
      addressCityName:
        findNameByCode(staticCitiesForState, baseData.addressCity) ||
        findNameByCode(cities, baseData.addressCity) ||
        dataFields.addressCityName,
      addressTownshipName:
        findNameByCode(staticTownshipsForCity, baseData.addressTownship) ||
        findNameByCode(townships, baseData.addressTownship) ||
        dataFields.addressTownshipName,
      birthPlaceName:
        findNameByCode(countries, baseData.birthPlace) ||
        baseData.birthPlaceName,
      originType: selectedOrigin,
      originCountryName:
        findNameByCode(countries, baseData.originCountry) ||
        baseData.originCountryName,
      economicActivityName:
        findNameByCode(economicActivities, baseData.economicActivity) ||
        baseData.economicActivityName,
      stockExchangeCountry: baseData.stockExchangeCountry,
      stockExchangeCountryName:
        findNameByCode(countries, baseData.stockExchangeCountry) ||
        baseData.stockExchangeCountryName,
      originDetail: data.legalEntityName,
    };

    const baseBeneficiaryData = parseFinalBeneficiaryData(data);
    const beneficiaryStateCatalog = staticStates.find(
      (item) => item.code === baseBeneficiaryData.stateId,
    );
    const beneficiaryCitiesForState =
      baseBeneficiaryData.countryId === COSTA_RICA
        ? staticCities.filter(
            (item) => item.parentId === beneficiaryStateCatalog?.id,
          )
        : [];
    const beneficiaryCityCatalog = beneficiaryCitiesForState.find(
      (item) => item.code === baseBeneficiaryData.cityId,
    );
    const beneficiaryTownshipsForCity = staticTownships.filter(
      (item) => item.parentId === beneficiaryCityCatalog?.id,
    );
    const beneficiaryBirthPlaceName =
      findNameByCode(beneficiaryBirthPlace, baseBeneficiaryData.birthPlaceId) ||
      baseBeneficiaryData.birthPlace;
    const beneficiaryEmission: FinalBeneficiary = {
      ...baseBeneficiaryData,
      country:
        findNameByCode(beneficiaryCountries, baseBeneficiaryData.countryId) ||
        baseBeneficiaryData.country,
      state:
        findNameByCode(beneficiaryCountryStates, baseBeneficiaryData.stateId) ||
        baseBeneficiaryData.state,
      city:
        findNameByCode(beneficiaryCitiesForState, baseBeneficiaryData.cityId) ||
        findNameByCode(beneficiaryCities, baseBeneficiaryData.cityId) ||
        baseBeneficiaryData.city,
      township:
        findNameByCode(
          beneficiaryTownshipsForCity,
          baseBeneficiaryData.townshipId,
        ) ||
        findNameByCode(beneficiaryTownships, baseBeneficiaryData.townshipId) ||
        baseBeneficiaryData.township,
      birthPlace: beneficiaryBirthPlaceName,
      nationality: beneficiaryBirthPlaceName,
      nationalityId: baseBeneficiaryData.birthPlaceId,
      occupationName: findNameByCode(
        beneficiaryOccupations,
        baseBeneficiaryData.occupation,
      ),
      economicActivityName: findNameByCode(
        economicActivities,
        baseBeneficiaryData.economicActivity,
      ),
    };
    setDataFieldsAction(emission);
    setFinalBeneficiaryAction(beneficiaryEmission);
    requestUpdateEmission(2);
  }

  /**
   * Note that the data managed by getInputProps mimics dataFields. To see
   * which values can be queried by getInputProps(inputName), check the
   * related data structure in EmissionSlice
   */
  const { getInputProps, handleSubmit, updateMultipleInputs, dataSource } =
    useFormHook(
      parseInitialFormData({
        dataFields,
        beneficiary: finalBeneficiary,
        personExists: personData.isValidPerson,
      }),
      handleFormSubmit,
      validationSchema,
      { deferErrorCheck: true },
    );

  /**
   * Values extracted from getInputProps, to facilitate data manipulation
   */
  const identificationType = getInputProps('idType').value as string;
  const identificationValue = getInputProps('idNumber').value as string;
  const birthPlaceValue = getInputProps('birthPlace').value as string;

  const { entityIsInvalid, messageLabel, formatError, idCheckRequested } =
    useLegalEntityValidator({
      entityData: personData,
      identificationNumber: identificationValue,
      identificationType,
      personSearchRequestStatus: requestingPerson,
      onPersonSearchRequested: validatePersonAction,
    });

  const {
    addressInformation: {
      countryOptions,
      stateOptions,
      requestingCountries,
      requestingStates,
      fetchCities,
      fetchTownships,
    },
    civilInformation: { civilStatusOptions, requestingCivilStatus },
    personIdData: {
      onLocalImageCaptured,
      disabled,
      failUpload,
      files,
      isFileUploaded,
    },
    classes,
    isMobile,
    wrapperClasses,
    requestingUpdateDataStatus,
    today,
    twoMonthsAhead,
    openValidationModal,
    setOpenValidationModal,
    openRestrictedCountryModal,
    setOpenRestrictedCountryModal,
    showValiationAlert,
    setShowValiationAlert,
    cityOptions,
    setCityOptions,
    townshipOptions,
    setTownshipOptions,
  } = usePersonDataCommon({
    dataFields,
    personData,
    isCollectiveQuotation,
    countries,
    countryStates,
    cities,
    townships,
    birthPlaces,
    civilStatus,
    restrictedBirthPlaces,
    getCountriesAction,
    getCountryStatesAction,
    getBirthPlacesAction,
    getRestrictedBirthPlacesAction,
    getCitiesAction,
    getTownshipsAction,
    setDataFieldsAction,
    onSubmit,
    resetRequestStatus,
    birthPlaceValue,
    personIsInvalid: entityIsInvalid,
    cedulaFileName,
    cedulaFileBase64,
    cedulaOptionalFileName,
    cedulaOptionalFileBase64,
    uploadCedulaAction,
    uploadCedulaOptionalAction,
    resetCedulaFilesAction,
    getInputProps,
    setSelectedOrigin,
  });

  const [invalidRucEntity, setInvalidRucEntity] = useState(false);

  let pep: FormValueTypes;
  let bloodRelatedToPep: FormValueTypes;
  let cicac: FormValueTypes;
  let pepLawArticle: FormValueTypes;

  if (isIndividualQuotation) {
    pep = getInputProps('pep').value;
    bloodRelatedToPep = getInputProps('bloodRelatedToPep').value;
    cicac = getInputProps('cicac').value;
    pepLawArticle = getInputProps('pepLawArticle').value;
  } else {
    pep = getInputProps('pep').value;
    bloodRelatedToPep = getInputProps('bloodRelatedToPep').value;
  }

  const isNotPep = useMemo(() => {
    const pepValues = isIndividualQuotation
      ? {
          pep: pep as boolean | undefined,
          bloodRelatedToPep: bloodRelatedToPep as boolean | undefined,
          cicac: cicac as boolean,
          pepLawArticle: pepLawArticle as boolean | undefined,
        }
      : {
          pep: pep as boolean | undefined,
          bloodRelatedToPep: bloodRelatedToPep as boolean | undefined,
        };
    return resolvesToNotPep(pepValues);
  }, [pep, bloodRelatedToPep, cicac, pepLawArticle, isIndividualQuotation]);

  /**
   * Since we are including personData.exist as an invisible form field
   * for yup validation purposes, we need to update it inside getInputProps
   * whenever it changes
   */
  useEffect(() => {
    updateMultipleInputs({
      personExists: personData.isValidPerson,
      beneficiaryFirstName: finalBeneficiary.firstName,
      beneficiaryLastName: finalBeneficiary.lastName,
      beneficiaryIdType: finalBeneficiary.idType,
      beneficiaryIdNumber: finalBeneficiary.id,
      beneficiarySharePercentage: finalBeneficiary.sharePercentage,
      beneficiaryBirthdate: finalBeneficiary.birthdate,
      beneficiaryNationalityId: finalBeneficiary.nationalityId,
      beneficiaryNationality: finalBeneficiary.nationality,
      beneficiaryBirthPlaceId: finalBeneficiary.birthPlaceId,
      beneficiaryBirthPlace: finalBeneficiary.birthPlace,
      beneficiaryOccupation: finalBeneficiary.occupation,
      beneficiaryOccupationName: finalBeneficiary.occupationName,
      economicActivity: finalBeneficiary.economicActivity,
      economicActivityName: finalBeneficiary.economicActivityName,
      beneficiaryCompanyRole: finalBeneficiary.companyRole,
      beneficiaryPhone1: finalBeneficiary.phone1,
      beneficiaryPhone2: finalBeneficiary.phone2,
      beneficiaryAddressDetail: finalBeneficiary.addressDetail,
      beneficiaryCountryId: finalBeneficiary.countryId,
      beneficiaryCountry: finalBeneficiary.country,
      beneficiaryStateId: finalBeneficiary.stateId,
      beneficiaryState: finalBeneficiary.state,
      beneficiaryCityId: finalBeneficiary.cityId,
      beneficiaryCity: finalBeneficiary.city,
      beneficiaryTownshipId: finalBeneficiary.townshipId,
      beneficiaryTownship: finalBeneficiary.township,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personData]);

  const requestingaValidateShareholder = useNetworkObserver(
    REQUEST_SEARCH_VALIDATE_SHAREHOLDER,
  );
  const defaultError = { error: false, errorText: '' };
  const [showShareholderValiationAlert, setShowShareholderValiationAlert] =
    useState(false);
  const [currentForm, setCurrentForm] = useState(0);
  const [refetch, setRefetch] = useState(false);
  const { value, onChange } = getInputProps('totalShareHolder');
  const intialArray = Array(value as number).fill(0);
  const [shareholders, setShareholders] = useState<number[]>(intialArray);
  const [loadingShareholder, setLoadingShareholder] = useState<boolean[]>([
    false,
  ]);
  const [errors, setErrors] = useState<{ error: boolean; errorText: string }[]>(
    intialArray.length > 0
      ? intialArray.map(() => defaultError)
      : [defaultError],
  );
  const [loading, setLoading] = useState(false);
  const [beneficiaryCityOptions, setBeneficiaryCityOptions] = useState<
    DisplayItem[]
  >([]);
  const [beneficiaryTownshipOptions, setBeneficiaryTownshipOptions] = useState<
    DisplayItem[]
  >([]);

  const handleViewModal = (): void => {
    setOpenValidationModal(!shareholderValidateData.isValidPerson);
    setShowShareholderValiationAlert(!shareholderValidateData.isValidPerson);

    if (loading) setLoading(false);

    setErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      if (
        !shareholderValidateData.isValidPerson ||
        (shareholderValidateData.personExist &&
          !shareholderValidateData.personInnova)
      ) {
        newErrors[currentForm] = {
          error: !shareholderValidateData.isValidPerson,
          errorText: 'Ingrese una identificación válida',
        };
      }
      return newErrors;
    });
  };

  useEffect(() => {
    if (requestingaValidateShareholder === 'success') {
      handleViewModal();
    }
  }, [shareholderValidateData, requestingaValidateShareholder]);

  const showParticipationError = useMemo(() => {
    let total = 0;
    const totalShareHolder = getInputProps('totalShareHolder').value as number;
    const helperArray = new Array(totalShareHolder).fill(0);
    helperArray.forEach((_, index) => {
      const percentage = getInputProps(`share${index}participationRate`)
        .value as number;
      const subTotal = Number(percentage);
      total += subTotal;
    });
    return total > 100;
  }, [getInputProps]);

  const [hasShareholders, setHasShareholders] = useState<boolean>(false);

  const hasShareholdersValue = getInputProps('hasShareholders')
    .value as boolean;

  useEffect(() => {
    setHasShareholders(hasShareholdersValue);
  }, [hasShareholdersValue]);

  /**
   * Returns true if the submti button must be disabled
   */

  function buttonIsDisabled(): boolean {
    return (
      restrictedBirthPlaces.some((place) => place.code === birthPlaceValue) ||
      !isFileUploaded ||
      entityIsInvalid ||
      invalidRucEntity ||
      !idCheckRequested ||
      !isNotPep ||
      ['requesting', 'success'].includes(requestingUpdateDataStatus) ||
      showParticipationError ||
      errors.filter((item) => item.error).length > 0
    );
  }

  const {
    beneficiaryCountryOptions,
    beneficiaryStateOptions,
    beneficiaryNationalityOptions,
    beneficiaryBirthPlaceOptions,
    requestingBeneficiaryCountries,
    requestingBeneficiaryStates,
    requestingBeneficiaryNationality,
    requestingBeneficiaryBirthPlace,
  } = useBeneficiaryAddressInformation({
    beneficiaryCountries,
    beneficiaryCities,
    beneficiaryCountryStates,
    beneficiaryTownships,
    beneficiaryNationality,
    beneficiaryBirthPlace,
    getBeneficiaryCitiesAction,
    getBeneficiaryTownshipsAction,
  });

  const { beneficiaryOccupationOptions, requestingBeneficiaryOccupations } =
    useOccupationInformation({
      beneficiaryOccupations,
    });

  const { economicActivitiesOptions, requestingEconomicActivities } =
    useEconomicActivityInformation({
      economicActivities,
    });

  const resetLegalBeneficiaries = (): void => {
    setLegalBeneficiariesAction([]);
    setShowSaveLegalBeneficiaries(false);
  };

  const openLegalBeneficiariesDialog = (): void => {
    setOpenLegalBeneficiaries(true);
  };

  const hideLegalBeneficiariesAlert = (): void => {
    setShowSaveLegalBeneficiaries(false);
  };

  const handleCloseLegalBeneficiariesDialog = (): void => {
    setOpenLegalBeneficiaries(false);
    setShowSaveLegalBeneficiaries(false);
    const currentLegalBeneficiaries: LegalBeneficiary[] =
      legalBeneficiaries || [];
    if (currentLegalBeneficiaries.length === 0) {
      getInputProps('enableInheritor').onChange(false);
    }
  };

  const handleSubmitLegalBeneficiaries = (): void => {
    setOpenLegalBeneficiaries(false);
    setShowSaveLegalBeneficiaries(true);
  };

  useEffect(() => {
    if (banks.length === 0) {
      requestBanksAction();
    }
  }, [banks.length, requestBanksAction]);

  const handleSetCurrentTab = (newTab: number): void => {
    setCurrentPage(newTab);
  };

  const legalCreditorsPage = (
    <CreditorCard
      isDisabled={false}
      data-testid="creditor-card"
      bankList={banks}
      dataSource={dataSource}
      getInputProps={getInputProps}
      currencySelected={quotation.quotationProfile.typeOfMoney}
      insuranceValue={quotation.summary.insuranceValue}
    />
  );

  const legalBeneficiaryPage = (
    <LegalHeirsCards
      data-testid="assign-legal-beneficiaries"
      getInputProps={getInputProps}
      onSetBeneficiaries={openLegalBeneficiariesDialog}
      onSetLegalHeirs={resetLegalBeneficiaries}
      showSaveAlert={showSaveLegalBeneficiaries}
      onCloseSaveAlert={hideLegalBeneficiariesAlert}
    />
  );

  const pages = [legalBeneficiaryPage, legalCreditorsPage];

  const getBeneficiaryCreditorsTabs = (): string[] => {
    return ['Beneficiarios', 'Acreedores'];
  };

  const idNumber = getInputProps(`share${currentForm}id`).value as string;
  const idType = getInputProps(`share${currentForm}idType`).value as string;

  const addShareholder = useCallback(async () => {
    const person = await personRequest(idNumber);
    addShareholderEquifax({ person, getInputProps, index: currentForm });
    setLoadingShareholder((prev) => {
      const newPrev = [...prev];
      newPrev[currentForm] = false;
      return newPrev;
    });
  }, [requestingaValidateShareholder]);

  useEffect(() => {
    if (
      requestingaValidateShareholder === 'success' &&
      idType === 'N' &&
      shareholderValidateData.isValidPerson
    ) {
      addShareholder();
    }
  }, [requestingaValidateShareholder]);

  useEffect(() => {
    if (!shareholderValidateData.isValidPerson || idType !== 'N') {
      setLoadingShareholder((prev) => {
        const newPrev = [...prev];
        newPrev[currentForm] = false;
        return newPrev;
      });
    }
  }, [shareholderValidateData]);

  useEffect(() => {
    if (personData.personInnova) {
      addLegalPersonACSEL({
        legalPerson: personData.personInnova,
        dataFields,
        getInputProps,
        setNewDataFields,
        setCityOptions,
        setTownshipOptions,
      });
    }

    if (!personData.personInnova) {
      updateToInitialValues({
        updateMultipleInputs,
        getInputProps,
        dataFields,
      });
    }
  }, [requestingPerson]);

  return (
    <>
      <Grid container justifyContent="center">
        <PersonValidationModal
          data-testid="person-validation-modal"
          onClose={(): void => setOpenValidationModal(false)}
          open={openValidationModal}
        />
        <PersonValidationModal
          data-testid="restricted-country-validation-modal"
          onClose={(): void => setOpenRestrictedCountryModal(false)}
          open={openRestrictedCountryModal}
          message="Hemos validado la información brindada y no podemos continuar con la emisión de la póliza por este medio."
        />
        <Grid
          item
          xs={12}
          md={7}
          container
          spacing={2}
          className={wrapperClasses.pageContainer}
        >
          <Grid item xs={12}>
            <PersonValidationAlert
              data-testid="person-validation-alert"
              onCloseAlert={(): void => setShowValiationAlert(false)}
              showAlert={showValiationAlert}
            />
          </Grid>
          {/* Identification */}
          <Grid item xs={12}>
            <IdCard
              data-testid="id-card"
              dropdownOptions={{
                key: 'idType',
                types: legalEntityIdTypes,
              }}
              inputOptions={renderInputOptions(
                formatError,
                entityIsInvalid,
                requestingPerson,
              )}
              instructionOptions={renderInstructionOptions(messageLabel)}
              getInputProps={getInputProps}
            />
          </Grid>

          {/* General fields */}
          <Grid item xs={12}>
            {isIndividualQuotation ? (
              makeEnterpriseDataSection(
                getInputProps,
                entityIsInvalid,
                countryOptions,
                requestingCountries,
                economicActivitiesOptions,
                requestingEconomicActivities,
                requestingPerson === 'requesting',
              )
            ) : (
              <GeneralLegalFieldsCard
                data-testid="general-fields-card"
                getInputProps={getInputProps}
                isDisabled={entityIsInvalid}
              />
            )}
          </Grid>

          {isIndividualQuotation
            ? makeAddressDataSection({
                getInputProps,
                updateMultipleInputs,
                countryOptions,
                cityOptions,
                stateOptions,
                townshipOptions,
                requestingCountries,
                requestingStates,
                cardTitle: 'Dirección de la Empresa',
                detailLabel: 'Dirección detallada',
                personIsInvalid: entityIsInvalid,
                setCityOptions,
                setTownshipOptions,
                fetchCities,
                fetchTownships,
                isLoading: requestingPerson === 'requesting',
              })
            : null}

          {/* Funds Origin card */}
          {isIndividualQuotation
            ? makeFundsOriginDataSection(
                getInputProps,
                countryOptions,
                requestingCountries,
                entityIsInvalid,
                setSelectedOrigin,
              )
            : null}

          {/* Beneficiary card */}
          <Grid item xs={12}>
            <FinalBeneficiaryCard
              data-testid="beneficiary-card"
              prefixKey={PREFIX_KEY}
              getInputProps={getInputProps}
              updateMultipleInputs={updateMultipleInputs}
              idTypes={naturalPersonIdTypes}
              entityIsInvalid={entityIsInvalid}
              countryStates={countryStates}
              beneficiaryBirthPlaceOptions={beneficiaryBirthPlaceOptions}
              requestingBeneficiaryBirthPlace={requestingBeneficiaryBirthPlace}
              beneficiaryNationalityOptions={beneficiaryNationalityOptions}
              requestingBeneficiaryNationality={
                requestingBeneficiaryNationality
              }
              beneficiaryOccupationOptions={beneficiaryOccupationOptions}
              requestingBeneficiaryOccupations={
                requestingBeneficiaryOccupations
              }
              requestingBeneficiaryCountries={requestingBeneficiaryCountries}
              beneficiaryCountriesOptions={beneficiaryCountryOptions}
              requestingBeneficiaryStates={requestingBeneficiaryStates}
              beneficiaryStatesOptions={beneficiaryStateOptions}
              beneficiaryCityOptions={beneficiaryCityOptions}
              beneficiaryTownshipOptions={beneficiaryTownshipOptions}
              isIndividualQuotation={isIndividualQuotation}
              personValidateData={personValidateData}
              validatePersonAction={validateSearchPersonAction}
              setEntityIsInvalid={setInvalidRucEntity}
              setBeneficiaryCityOptions={setBeneficiaryCityOptions}
              setBeneficiaryTownshipOptions={setBeneficiaryTownshipOptions}
            />
          </Grid>

          {/* File Loader */}
          {isIndividualQuotation ? (
            <Grid item xs={12}>
              <MultiFileLoader
                onDeleteFile={resetCedulaFilesAction}
                onLocalImageCaptured={onLocalImageCaptured}
                title="Foto del documento de identidad y personería jurídica"
                subTitle="Por favor sube una fotografía por ambos lados de tu cédula jurídica"
                confirmationMessage="¿Estás seguro que deseas eliminar el documento?"
                files={files}
                maxFiles={2}
                disabled={disabled}
                deleteButtonId="delete-id-cedula"
                id="test-cedula-component"
                failUpload={failUpload}
                data-testid="additional-document-card"
              />
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <PersonValidationAlert
              data-testid="shareholder-validation-alert"
              onCloseAlert={(): void => setShowShareholderValiationAlert(false)}
              showAlert={showShareholderValiationAlert}
            />
          </Grid>

          {isIndividualQuotation
            ? makeShareholderDataSection(
                getInputProps,
                countryOptions,
                requestingCountries,
                showParticipationError,
                civilStatusOptions,
                requestingCivilStatus,
                hasShareholders,
                entityIsInvalid,
                setCurrentForm,
                currentForm,
                shareholders,
                setShareholders,
                errors,
                setErrors,
                validateSearchShareholderAction,
                setLoading,
                refetch,
                setRefetch,
                onChange,
                loadingShareholder,
                setLoadingShareholder,
              )
            : null}

          {!isIndividualQuotation && (
            <Grid item xs={12} md={6}>
              <DefaultCard
                data-testid="insurance-start-day"
                title="¿Cuándo empieza la protección de tu seguro?"
                forcedMinHeight={minDesktopCardHeight + 10}
              >
                <DateField
                  inputId="sales-web-inspection-input-effectiveDate"
                  inputName="effectiveDate"
                  data-testid="effectiveDate"
                  inputLabel="Fecha de inicio vigencia"
                  minDate={twoMonthsAhead}
                  maxDate={today}
                  dataSource={dataSource<string>()}
                  fallbackValue={undefined}
                  autoConstraint
                  isDisabled={
                    (getInputProps('assignDateSignature').value as boolean) ||
                    entityIsInvalid
                  }
                />
                <CheckBox
                  inputId="sales-web-inspection-checkbox-assignDateSignature"
                  data-testid="onChangeAssignDateSignature"
                  checkTitle={
                    <span
                      id="sales-web-inspection-label-assignDateSignature"
                      style={{ fontSize: 15, fontWeight: 700 }}
                    >
                      Asignar la fecha conforme a la firma de documentos con
                      cliente
                    </span>
                  }
                  checkName="assignDateSignature"
                  defaultValue={
                    getInputProps('assignDateSignature').value as boolean
                  }
                  onChange={(val): void => {
                    getInputProps('assignDateSignature').onChange(val);
                    getInputProps('effectiveDate').onChange(
                      val ? format(today, 'dd/MM/yyyy') : '',
                    );
                  }}
                  style={{ alignItems: 'center', paddingTop: 15 }}
                  disabled={entityIsInvalid}
                />
              </DefaultCard>
            </Grid>
          )}

          {/* PepCard */}
          <Grid item xs={12} md={isIndividualQuotation ? 12 : 6}>
            <PepCard
              data-testid="pep-card"
              getInputProps={getInputProps}
              isDisabled={entityIsInvalid}
              isMobile={isMobile}
              useLegalEntityLayout
              isIndividualQuotation={isIndividualQuotation}
            />
          </Grid>

          <Grid item xs={12} md={12}>
            <DefaultCard data-testid="Other-information-cards">
              <Grid container alignItems="center">
                {!isIndividualQuotation && legalBeneficiaryPage}
                {isIndividualQuotation && (
                  <>
                    <Grid item xs={12}>
                      <Box display="flex" sx={{ width: '100%' }}>
                        <Box>
                          <span className={classes.title}>
                            Información adicional
                          </span>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} style={{ paddingTop: 10 }}>
                      <Tabs
                        data-testid="tabs"
                        tabs={getBeneficiaryCreditorsTabs()}
                        currentTab={currentPage}
                        onTabChange={handleSetCurrentTab}
                      />
                      <PaddedContent
                        style={{ width: '100%', padding: 0, paddingTop: 10 }}
                      >
                        {pages[currentPage]}
                      </PaddedContent>
                    </Grid>
                  </>
                )}
              </Grid>
            </DefaultCard>
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12} container justifyContent="center">
            <div className={wrapperClasses.buttonContainer}>
              <DefaultButton
                id="sales-web-inspection-button-next"
                data-testid="submit-button"
                onClick={handleSubmit}
                fullWidth
                disabled={buttonIsDisabled()}
              >
                {requestingUpdateDataStatus === 'requesting' ? (
                  <CircularProgress data-testid="spinner-btn" />
                ) : (
                  'Siguiente'
                )}
              </DefaultButton>
            </div>
          </Grid>
        </Grid>
      </Grid>
      <BeneficiaryDialog
        data-testid="beneficiary-dialog"
        open={openLegalBeneficiaries}
        onCloseDialog={handleCloseLegalBeneficiariesDialog}
        onSubmitBeneficiaries={handleSubmitLegalBeneficiaries}
      />
    </>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  cedulaFileName: string;
  cedulaFileBase64: string;
  cedulaOptionalFileName: string;
  cedulaOptionalFileBase64: string;
  dataFields: EmissionDataFields;
  finalBeneficiary: FinalBeneficiary;
  quotation: Quotation;
  personData: PersonDataResponse;
  banks: GeneralCatalogItem[];
  countries: GeneralCatalogItem[];
  cities: GeneralCatalogItem[];
  countryStates: GeneralCatalogItem[];
  townships: GeneralCatalogItem[];
  beneficiaryCountries: GeneralCatalogItem[];
  beneficiaryCities: GeneralCatalogItem[];
  beneficiaryCountryStates: GeneralCatalogItem[];
  beneficiaryTownships: GeneralCatalogItem[];
  beneficiaryBirthPlace: GeneralCatalogItem[];
  beneficiaryNationality: GeneralCatalogItem[];
  beneficiaryOccupations: GeneralCatalogItem[];
  economicActivities: GeneralCatalogItem[];
  birthPlaces: GeneralCatalogItem[];
  restrictedBirthPlaces: GeneralCatalogItem[];
  civilStatus: GeneralCatalogItem[];
  legalBeneficiaries?: LegalBeneficiary[];
  personValidateData: PersonDataResponse;
  shareholderValidateData: PersonDataResponse;
} => ({
  cedulaFileName: state.emissionPolicy.identificationFileName,
  cedulaFileBase64: state.emissionPolicy.identificationBase64,
  cedulaOptionalFileName: state.emissionPolicy.identificationOptionalFileName,
  cedulaOptionalFileBase64: state.emissionPolicy.identificationOptionalBase64,
  dataFields: state.emissionPolicy.dataFields,
  finalBeneficiary: state.emissionPolicy.finalBeneficiary,
  quotation: state.quotation.quotation,
  personData: state.person.personData,
  banks: state.creditorCatalog.banks,
  countries: state.legalEntityCatalog.countries,
  cities: state.legalEntityCatalog.cities,
  countryStates: state.legalEntityCatalog.countryStates,
  townships: state.legalEntityCatalog.townships,
  beneficiaryCountries: state.legalEntityCatalog.beneficiaryCountries,
  beneficiaryCities: state.legalEntityCatalog.beneficiaryCities,
  beneficiaryCountryStates: state.legalEntityCatalog.beneficiaryCountryStates,
  beneficiaryTownships: state.legalEntityCatalog.beneficiaryTownships,
  beneficiaryBirthPlace: state.legalEntityCatalog.beneficiaryBirthPlace,
  beneficiaryNationality: state.legalEntityCatalog.beneficiaryNationality,
  beneficiaryOccupations: state.legalEntityCatalog.beneficiaryOccupations,
  economicActivities: state.legalEntityCatalog.economicActivities,
  birthPlaces: state.legalEntityCatalog.birthPlaces,
  restrictedBirthPlaces: state.legalEntityCatalog.restrictedBirthPlaces,
  civilStatus: state.legalEntityCatalog.civilStatus,
  legalBeneficiaries: state.emissionPolicy.legalBeneficiaries,
  personValidateData: state.personValidate.personValidateData,
  shareholderValidateData: state.shareholderValidate.shareholderValidateData,
});

const reduxActions = {
  setDataFieldsAction: setDataFields,
  setNewDataFieldsAction: setNewDataFields,
  setFinalBeneficiaryAction: setFinalBeneficiary,
  requestBanksAction: requestBanks,
  requestUpdateEmission: updateEmissionByPage,
  resetRequestStatus: setRequestStatus,
  getCountriesAction: getCountries,
  getCountryStatesAction: getCountryStates,
  getCitiesAction: getCities,
  getTownshipsAction: getTownships,
  getBeneficiaryCountriesAction: getBeneficiaryCountries,
  getBeneficiaryCountryStatesAction: getBeneficiaryCountryStates,
  getBeneficiaryCitiesAction: getBeneficiaryCities,
  getBeneficiaryTownshipsAction: getBeneficiaryTownships,
  getBeneficiaryBirthPlaceAction: getBeneficiaryBirthPlace,
  getBeneficiaryOccupationsAction: getBeneficiaryOccupations,
  getEconomicActivitiesAction: getEconomicAcitivies,
  getBeneficiaryNationalityAction: getBeneficiaryNationality,
  validatePersonAction: requestSearchPerson,
  getBirthPlacesAction: getBirthPlaces,
  getRestrictedBirthPlacesAction: getRestrictedBirthPlaces,
  getCivilStatusAction: getCivilStatus,
  setLegalBeneficiariesAction: setLegalBeneficiaries,
  uploadCedulaAction: setIdentificationBase64,
  uploadCedulaOptionalAction: setIdentificationOptionalBase64,
  resetCedulaFilesAction: resetCedulaFiles,
  validateSearchPersonAction: requestSearchValidatePerson,
  validateSearchShareholderAction: requestSearchValidateShareholder,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onSubmit: () => void;
}

export default connector(LegalPersonData);
